import React, { useEffect, useState } from 'react';
import { Archive, Check, CheckCircle2, Lock, Shield } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { KontoAktionen } from './KontoAktionen';
import { rollenName } from '../../lib/rollen';
import { datumKurz, istGesperrt, kontoStatus, letzteAnmeldungText } from '../../lib/verwaltungskonten';

const BREIT = '(min-width: 1024px)';
const useBreit = () => {
  const [breit, setBreit] = useState(() => window.matchMedia(BREIT).matches);
  useEffect(() => {
    const mq = window.matchMedia(BREIT);
    const neu = () => setBreit(mq.matches);
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);
  return breit;
};

export const StatusAbzeichen = ({ konto, ort }) => {
  const status = kontoStatus(konto);
  const testid = ort ? `${ort}-status-${konto.id}` : `status-badge-${konto.id}`;
  if (status === 'archiviert') {
    return (
      <div data-testid={testid}>
        <Badge className="bg-slate-200 text-slate-600"><Archive className="mr-1 h-3 w-3" />Archiviert</Badge>
        {istGesperrt(konto) && <p className="mt-0.5 text-xs text-slate-500">gesperrt</p>}
      </div>
    );
  }
  if (status === 'gesperrt') {
    return (
      <div data-testid={testid}>
        <Badge className="bg-red-100 text-red-700"><Lock className="mr-1 h-3 w-3" />Gesperrt</Badge>
        {konto.gesperrt_am && <p className="mt-0.5 text-xs text-slate-500">seit {datumKurz(konto.gesperrt_am)}</p>}
      </div>
    );
  }
  return <Badge className="bg-green-100 text-green-700" data-testid={testid}><CheckCircle2 className="mr-1 h-3 w-3" />Aktiv</Badge>;
};

export const ZweiFaktorZelle = ({ konto }) => {
  if (kontoStatus(konto) === 'archiviert') return <span className="text-slate-400" data-testid={`zwei-faktor-${konto.id}`}>–</span>;
  if (konto.zwei_faktor_aktiv) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-green-700" data-testid={`zwei-faktor-${konto.id}`}>
        <Check className="h-4 w-4" />{konto.zwei_faktor_seit ? `Aktiv seit ${datumKurz(konto.zwei_faktor_seit)}` : 'Aktiv'}
      </span>
    );
  }
  return <span className="text-sm text-slate-500" data-testid={`zwei-faktor-${konto.id}`}>Nicht eingerichtet</span>;
};

const KeinZugang = ({ konto, dringend, klickbar, onKlick }) => {
  const tage = konto.created_at ? Math.floor((Date.now() - new Date(konto.created_at).getTime()) / 86400000) : -1;
  const klasse = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${dringend ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`;
  const inhalt = <>Kein Zugang nachweisbar{tage >= 0 && <span className="opacity-70">· seit {tage} Tagen</span>}</>;
  if (!klickbar) return <span className={klasse} data-testid={`never-logged-in-badge-${konto.id}`}>{inhalt}</span>;
  return (
    <button type="button" onClick={() => onKlick(konto)} className={`${klasse} ${dringend ? 'hover:bg-red-200' : 'hover:bg-slate-200'}`}
      title="Kein Zugang nachweisbar — klicken, um Zugang einzurichten" data-testid={`never-logged-in-badge-${konto.id}`}>
      {inhalt}
    </button>
  );
};

const Benutzer = ({ konto, noAccessMap, darfVerwalten, onKeinZugang, onName }) => (
  <div className="flex min-w-0 items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100"><Shield className="h-4 w-4 text-blue-800" /></div>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onName(konto)} className="text-left font-medium text-slate-900 hover:text-blue-800 hover:underline" data-testid={`konto-name-${konto.id}`}>
          {konto.first_name} {konto.last_name}
        </button>
        {noAccessMap.has(konto.id) && konto.is_active !== false && (
          <KeinZugang konto={konto} dringend={noAccessMap.get(konto.id) === true} klickbar={darfVerwalten(konto) && !istGesperrt(konto)} onKlick={onKeinZugang} />
        )}
      </div>
      <p className="break-all text-xs text-slate-500">{konto.email}</p>
    </div>
  </div>
);

const LetzteAnmeldung = ({ konto, noAccessMap }) => (
  <span className="text-sm text-slate-600" title={datumKurz(konto.last_login_at)} data-testid={`letzte-anmeldung-${konto.id}`}>
    {letzteAnmeldungText(konto.last_login_at, new Date(), noAccessMap.get(konto.id) === true)}
  </span>
);

const Rolle = ({ konto }) => <Badge className="bg-blue-100 text-blue-700">{rollenName(konto.role)}</Badge>;

// Liste „Verwaltende Rollen“: ab 1024 px Tabelle, darunter Karten (nie beides im DOM).
export const KontoTabelle = ({ liste, ...rest }) => {
  const breit = useBreit();
  const { ich, onAktion } = rest;
  const gedimmt = (k) => (istGesperrt(k) && k.is_active !== false ? 'bg-slate-50/80 text-slate-600' : k.is_active === false ? 'opacity-60' : '');
  if (!breit) {
    return (
      <div className="space-y-3" data-testid="konto-karten">
        {liste.map((k) => (
          <div key={k.id} className={`space-y-3 rounded-lg border p-3 ${gedimmt(k)}`} data-testid={`user-row-${k.id}`}>
            <Benutzer konto={k} {...rest} />
            <div className="flex flex-wrap items-center gap-2"><Rolle konto={k} /><StatusAbzeichen konto={k} /></div>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
              <div>2FA<div className="mt-0.5"><ZweiFaktorZelle konto={k} /></div></div>
              <div>Letzte Anmeldung<div className="mt-0.5"><LetzteAnmeldung konto={k} {...rest} /></div></div>
            </div>
            <KontoAktionen konto={k} ich={ich} onAktion={onAktion} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <Table data-testid="konto-tabelle">
      <TableHeader>
        <TableRow>
          <TableHead>Benutzer</TableHead><TableHead>Rolle</TableHead><TableHead>Status</TableHead>
          <TableHead>2FA</TableHead><TableHead>Letzte Anmeldung</TableHead><TableHead className="text-right">Aktionen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {liste.map((k) => (
          <TableRow key={k.id} className={gedimmt(k)} data-testid={`user-row-${k.id}`}>
            <TableCell><Benutzer konto={k} {...rest} /></TableCell>
            <TableCell><Rolle konto={k} /></TableCell>
            <TableCell><StatusAbzeichen konto={k} /></TableCell>
            <TableCell><ZweiFaktorZelle konto={k} /></TableCell>
            <TableCell><LetzteAnmeldung konto={k} {...rest} /></TableCell>
            <TableCell><div className="flex justify-end"><KontoAktionen konto={k} ich={ich} onAktion={onAktion} /></div></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default KontoTabelle;
